import React, { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from '../../../app/LanguageContext';
import { buildVariantTitle } from './productVariantUtils';

export default function LegacyVariantsPanel({
  variants = [],
  legacyCount = 0,
  options = [],
  onVariantClick,
  onPurgeUnused,
  purging = false,
}) {
  const { translate } = useTranslation();
  const [open, setOpen] = useState(false);

  const totalCount = legacyCount > 0 ? legacyCount : variants.length;
  if (totalCount <= 0) return null;

  const previewHidden = Math.max(0, totalCount - variants.length);

  return (
    <Box
      sx={{
        mt: 1,
        px: 1,
        py: 0.5,
        borderTop: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            cursor: 'pointer',
            color: 'text.disabled',
            userSelect: 'none',
            minWidth: 0,
            flex: 1,
            '&:hover': { color: 'text.secondary' },
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
            }}
          />
          <Typography variant="caption" noWrap>
            {translate('products.variant_legacy_toggle').replace('{count}', String(totalCount))}
          </Typography>
        </Box>
        {onPurgeUnused ? (
          <Button
            type="button"
            size="small"
            variant="text"
            color="inherit"
            disabled={purging}
            onClick={(event) => {
              event.stopPropagation();
              onPurgeUnused();
            }}
            sx={{
              flexShrink: 0,
              minWidth: 'auto',
              px: 0.75,
              py: 0,
              fontSize: '0.75rem',
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
            }}
            title={translate('products.purge_unused_hint')}
          >
            {purging ? translate('common.saving') : translate('products.purge_unused_label')}
          </Button>
        ) : null}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25, listStyle: 'none' }}>
          {variants.map((variant) => (
            <Box component="li" key={variant.id} sx={{ py: 0.125 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  cursor: onVariantClick ? 'pointer' : 'default',
                  '&:hover': onVariantClick ? { textDecoration: 'underline' } : undefined,
                }}
                onClick={() => onVariantClick?.(variant)}
              >
                {buildVariantTitle(variant, options)}
              </Typography>
            </Box>
          ))}
          {previewHidden > 0 ? (
            <Box component="li" sx={{ py: 0.125 }}>
              <Typography variant="caption" color="text.disabled">
                {translate('products.variant_legacy_preview_more').replace('{count}', String(previewHidden))}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
